import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";

export interface IListDiscount {
  active: boolean;
  cap_per_head: number;
  code: string;
  id?: ID;
  message: string;
  percentage: number;
  balance?: number;
}

export interface IDiscount extends IListDiscount {
  applicable_to: number;
  applicable_weekdays: number[];
  cap: null | number;
  discounted_menu_only: boolean;
  end_date: string;
  end_time: string;
  service_charge_on_discount: boolean;
  start_date: string;
  start_time: string;
  timezone: string;
}

export interface IListDiscountParams {
  search?: string;
  isVenueSpecific?: boolean;
  page?: number;
  page_size?: number;
}

export interface IListDiscountResponse
  extends APIPaginatedResponse<IListDiscount> {}

export interface IListDiscountResponseAll {
  pages: Array<IListDiscountResponse>;
  pageParams: number[];
}

export interface IDiscountService {
  list: (
    params?: IListDiscountParams,
  ) => UseQueryResult<Array<IListDiscount>, Error>;
  listAll: (
    params?: IListDiscountParams,
  ) => UseInfiniteQueryResult<IListDiscountResponseAll, Error>;
}

export class DiscountService implements IDiscountService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(
    params?: IListDiscountParams,
  ): UseQueryResult<Array<IListDiscount>, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );

    const { isVenueSpecific = false, ...restParams } = updatedParams;
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "discounts", "list"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async () => {
        const {
          data: { results, next, count, previous },
        } = await this.api.get<AxiosResponse<IListDiscountResponse>>(
          "/promotions/discount_codes/",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: isVenueSpecific,
            params: restParams,
          },
        );

        return {
          results,
          next,
          count,
          previous,
        };
      },
    });
  }

  listAll(
    params?: IListDiscountParams,
  ): UseInfiniteQueryResult<IListDiscountResponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );

    const { isVenueSpecific = false, ...restParams } = updatedParams;
    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "discounts", "list-all"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<
          AxiosResponse<IListDiscountResponse>
        >("/promotions/discount_codes/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: isVenueSpecific,
          params: { ...restParams, page: pageParam, page_size: 50 },
        });

        return data;
      },
      getNextPageParam: (lastPage: IListDiscountResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }
}
